// components/Chat/ProductSelection.js
import React, { useState } from 'react';
import styles from '../../ChatApp.module.css';
import { 
    getProductName, 
    getProductPrice, 
    getProductDescription, 
    getProductCategory, 
    getProductUnit, 
    getProductWeight, 
    getProductImageUrl,
    getProductSalesStats // Import the new helper
} from '../../utils/AppHelpers';

const ProductSelection = React.memo(({ products, onAddToCart, onQuickAdd, onImageClick }) => {
    const [quantities, setQuantities] = useState({});
    const [addedSKUs, setAddedSKUs] = useState(new Set());
    
    const handleQuantityChange = (sku, value) => {
        const quantity = parseInt(value, 10);
        setQuantities(prev => ({ ...prev, [sku]: quantity >= 0 ? quantity : 0 }));
    };
    const handleAddToCartClick = () => {
        onAddToCart(quantities, products);
        setAddedSKUs(new Set(Object.keys(quantities)));
    };
    const handleQuickAddClick = (product) => {
        onQuickAdd(product);
        setAddedSKUs(prev => new Set(prev).add(product.sku));
    };
    
    return (
        <div className={styles.productSelectionContainer}>
            {products.map(product => {
                const description = getProductDescription(product);
                const category = getProductCategory(product);
                const unit = getProductUnit(product);
                const weight = getProductWeight(product);
                const imageUrl = getProductImageUrl(product);
                const salesStats = getProductSalesStats(product); // Check for sales data
                const isAdded = addedSKUs.has(product.sku);
                
                return (
                    <div key={product.sku} className={styles.productItem}>
                        <img
                            src={imageUrl}
                            alt={getProductName(product)}
                            className={styles.productImage}
                            onClick={() => imageUrl && onImageClick(imageUrl)}
                            title="Click to enlarge"
                            onError={(e) => {
                                e.target.src = `https://via.placeholder.com/300x300.png?text=No+Img`;
                                e.target.onerror = null;
                            }}
                        />
                        <div className={styles.productInfo}>
                            <div className={styles.productName}>{getProductName(product)}</div>
                            
                            {/* INTELLIGENT SALES STAT DISPLAY */}
                            {salesStats && (
                                <div style={{
                                    display: 'inline-block',
                                    backgroundColor: '#e6f4ea',
                                    color: '#1e8e3e',
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    padding: '2px 6px',
                                    borderRadius: '12px',
                                    marginTop: '4px',
                                    marginBottom: '4px'
                                }}>
                                    🔥 {salesStats} units sold
                                </div>
                            )}

                            <div className={styles.productPrice}>${getProductPrice(product).toFixed(2)}</div>
                            <div className={styles.productDetails}>
                                {description && <div className={styles.productDescription}>{description}</div>}
                                <div className={styles.productMeta}>
                                    {category && <div><strong>Category:</strong> {category}</div>}
                                    {unit && <div><strong>Unit:</strong> {unit}</div>}
                                    {weight && <div><strong>Weight:</strong> {weight}</div>}
                                </div>
                            </div>
                        </div>
                        <div className={styles.productActions}>
                            <input type="number" min="0" className={styles.quantityInput} value={quantities[product.sku] || ''} onChange={(e) => handleQuantityChange(product.sku, e.target.value)} placeholder="0" disabled={isAdded} />
                        </div>
                        <button className={`${styles.quickAddButton} ${isAdded ? styles.disabledButton : ''}`} onClick={() => handleQuickAddClick(product)} disabled={isAdded}>
                            {isAdded ? '✔' : '+1'}
                        </button>
                    </div>
                );
            })}
            <button className={`${styles.addToCartButton} ${addedSKUs.size > 0 ? styles.disabledButton : ''}`} onClick={handleAddToCartClick} disabled={addedSKUs.size > 0}>
                {addedSKUs.size > 0 ? '✔ Added' : 'Add Selected to Cart'}
            </button>
        </div>
    );
});

export default ProductSelection;